import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "sonner";
import { hc } from "@/frontend/lib/hc";
import {
  answerCallOffer,
  applyCallAnswer,
  callMediaErrorMessage,
  closeCallMedia,
  createCallOffer,
  setCallMuted,
  type CallMedia,
  type CallMediaEvents,
} from "@/frontend/lib/whatsapp-call-media";
import { callPreviewText } from "@/shared/whatsapp-calling";
import type { PublicCall } from "@/backend/handlers/calls";

export type { PublicCall };

type CallDetail = { call: PublicCall; offerSdp: string | null; answerSdp: string | null; ringingForMe?: boolean };
type ActiveCall = {
  call: PublicCall;
  media: CallMedia;
  muted: boolean;
  // Brief network trouble the browser is recovering from
  reconnecting: boolean;
  // When this browser saw the call connect; the timer counts from here, not the server's clock
  connectedAtLocal: number | null;
};
// An outgoing call between the click and Meta confirming it
type Dialing = { conversationId: string; label: string; cancelled: boolean };

const LIVE_STATUSES = ["ringing", "connected"];
// How long the "Call ended" card stays up
const ENDED_CARD_MS = 4000;
const orgHeaders = (orgId: string) => ({ headers: { "X-Organization-Id": orgId } });

export function callerLabel(call: Pick<PublicCall, "customerName" | "customerWaId">) {
  return call.customerName || `+${call.customerWaId}`;
}

interface CallState {
  orgId: string | null;
  // The workspace has a WhatsApp Business number that takes calls; null until known
  callingAvailable: boolean | null;
  // Chat identities (business numbers) that take calls, to show the call button only on those chats
  callableIdentities: string[];
  // Inbound calls ringing for this teammate, newest first
  ringing: PublicCall[];
  // Why answering a ringing call failed, shown on its card so the teammate can fix it and retry
  answerError: { callId: string; message: string } | null;
  active: ActiveCall | null;
  dialing: Dialing | null;
  // The call that just ended, with how long it lasted here, for a short summary card
  ended: { call: PublicCall; seconds: number | null } | null;
  pending: boolean;
  // Ticks once a second while a call is up, for the call timer
  now: number;
  // The teammate asked not to be rung; null until loaded
  away: boolean | null;
  missedCount: number;
  setOrg: (orgId: string | null) => void;
  setAvailability: (available: boolean, identities: string[]) => void;
  // Re-asks whether any number takes calls, e.g. after calling is turned on or off
  loadAvailability: () => Promise<void>;
  loadRinging: () => Promise<void>;
  handleUpdate: (update: { callId: string; status: string; direction: string }, canAnswer: boolean) => Promise<void>;
  handlePermissionReply: (reply: { conversationId: string; granted: boolean }) => void;
  answer: (callId: string) => Promise<void>;
  dismiss: (callId: string) => Promise<void>;
  declineForEveryone: (callId: string) => Promise<void>;
  placeCall: (conversationId: string, label: string) => Promise<"placed" | "no_permission" | "failed">;
  cancelDialing: () => void;
  hangUp: () => Promise<void>;
  syncActive: () => Promise<void>;
  // Hangs up a live call when the tab closes, so the customer isn't left on a silent line
  endOnUnload: () => void;
  toggleMute: () => void;
  clearEnded: () => void;
  loadAway: () => Promise<void>;
  setAway: (away: boolean) => Promise<void>;
  refreshMissed: () => Promise<void>;
}

// When this teammate last opened the call log, per workspace; drives the missed-call badge
export const useCallsSeenStore = create<{ seenAt: Record<string, string>; markSeen: (orgId: string) => void }>()(
  persist(
    (set) => ({
      seenAt: {},
      markSeen: (orgId) => set((state) => ({ seenAt: { ...state.seenAt, [orgId]: new Date().toISOString() } })),
    }),
    { name: "querygen-calls-seen" },
  ),
);

const callingCacheKey = (orgId: string) => `querygen-calling-available:${orgId}`;

/** The workspace's last known calling availability, so the Calls menu item doesn't pop in late. */
export function readCachedCallingAvailable(orgId: string | undefined): boolean | null {
  if (!orgId) return null;
  try {
    const value = window.localStorage.getItem(callingCacheKey(orgId));
    return value === null ? null : value === "true";
  } catch {
    return null;
  }
}

async function errorMessage(response: Response, fallback: string) {
  const body = await response.json().catch(() => ({})) as { error?: string };
  return body.error || fallback;
}

function endOnServer(orgId: string, callId: string, keepalive = false) {
  // keepalive lets the request finish while the tab is closing
  return fetch(`/api/calls/${encodeURIComponent(callId)}/end`, {
    method: "POST",
    credentials: "include",
    headers: { "X-Organization-Id": orgId },
    keepalive,
  });
}

function outcomeToast(call: PublicCall) {
  if (call.status === "failed") toast.error(call.errorMessage || callPreviewText(call));
  else if (call.status !== "completed") toast.info(`${callPreviewText(call)} · ${callerLabel(call)}`);
}

async function fetchCall(orgId: string, callId: string): Promise<CallDetail | null> {
  const response = await hc.api.calls[":callId"].$get({ param: { callId } }, orgHeaders(orgId));
  return response.ok ? await response.json() as CallDetail : null;
}

// A desktop notification for a call ringing while the tab is in the background
function notifyIncomingCall(call: PublicCall) {
  if (typeof document === "undefined" || !document.hidden || typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  const notification = new Notification("Incoming WhatsApp call", {
    body: `${callerLabel(call)} is calling ${call.businessName || call.businessPhone || "your WhatsApp number"}`,
    tag: `call-${call.id}`,
    requireInteraction: true,
  });
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
}

/** Asks once for desktop notifications, from a click, so background calls can be announced. */
export function requestCallNotifications() {
  if (typeof Notification !== "undefined" && Notification.permission === "default") {
    void Notification.requestPermission().catch(() => undefined);
  }
}

export const useCallStore = create<CallState>((set, get) => {
  const mediaEvents = (getMedia: () => CallMedia | null): CallMediaEvents => ({
    onFailed: () => {
      const media = getMedia();
      if (media && get().active?.media === media) {
        toast.error("The call's audio connection dropped");
        void get().hangUp();
      }
    },
    onReconnecting: (reconnecting) => {
      const active = get().active;
      if (active && active.media === getMedia()) set({ active: { ...active, reconnecting } });
    },
  });

  const finish = (call: PublicCall) => {
    const connectedAt = get().active?.connectedAtLocal;
    const seconds = call.durationSeconds ?? (connectedAt ? Math.round((Date.now() - connectedAt) / 1000) : null);
    set({ active: null, ended: { call, seconds } });
    window.setTimeout(() => {
      if (get().ended?.call.id === call.id) set({ ended: null });
    }, ENDED_CARD_MS);
  };

  return {
    orgId: null,
    callingAvailable: null,
    callableIdentities: [],
    ringing: [],
    answerError: null,
    active: null,
    dialing: null,
    ended: null,
    pending: false,
    now: Date.now(),
    away: null,
    missedCount: 0,

    setOrg: (orgId) => {
      const { orgId: previousOrgId, active } = get();
      if (previousOrgId === orgId) return;
      closeCallMedia(active?.media);
      // Leaving the workspace hangs up rather than leaving the customer on a silent line
      if (previousOrgId && active) void endOnServer(previousOrgId, active.call.id).catch(() => undefined);
      set({
        orgId,
        callingAvailable: null,
        callableIdentities: [],
        ringing: [],
        answerError: null,
        active: null,
        dialing: null,
        ended: null,
        pending: false,
        away: null,
        missedCount: 0,
      });
    },

    setAvailability: (available, identities) => {
      const orgId = get().orgId;
      try {
        if (orgId) window.localStorage.setItem(callingCacheKey(orgId), String(available));
      } catch {
        // Only the menu's first paint depends on it
      }
      set({ callingAvailable: available, callableIdentities: identities });
    },

    loadAvailability: async () => {
      const orgId = get().orgId;
      if (!orgId) return;
      try {
        const response = await hc.api.calls.availability.$get({ query: { organizationId: orgId } });
        const body = response.ok ? await response.json() as { available: boolean; identities: string[] } : null;
        if (get().orgId === orgId) get().setAvailability(Boolean(body?.available), body?.identities ?? []);
      } catch {
        // Keeps the last known availability
      }
    },

    // A page opened mid-ring still shows the call
    loadRinging: async () => {
      const orgId = get().orgId;
      if (!orgId) return;
      const response = await hc.api.calls.$get({ query: { organizationId: orgId, status: "ringing", direction: "inbound" } });
      if (!response.ok) return;
      const { calls } = await response.json() as { calls: PublicCall[] };
      // The offer is only handed to teammates the call is ringing for
      const details = await Promise.all(calls.map((call) => fetchCall(orgId, call.id)));
      const ringingForMe = details.flatMap((detail) => detail?.offerSdp ? [detail.call] : []);
      if (get().orgId === orgId) set({ ringing: ringingForMe });
    },

    handleUpdate: async (update, canAnswer) => {
      const { orgId, active } = get();
      if (!orgId) return;

      if (active?.call.id === update.callId) {
        await get().syncActive();
        return;
      }

      if (update.direction === "inbound" && update.status === "missed") void get().refreshMissed();

      const wasRinging = get().ringing.find((call) => call.id === update.callId);
      if (update.direction === "inbound" && update.status === "ringing" && canAnswer) {
        const detail = await fetchCall(orgId, update.callId);
        if (get().orgId !== orgId) return;
        if (detail?.offerSdp && detail.call.status === "ringing") {
          const known = get().ringing.some((call) => call.id === update.callId);
          set({ ringing: known
            ? get().ringing.map((call) => call.id === update.callId ? detail.call : call)
            : [detail.call, ...get().ringing] });
          if (!known) notifyIncomingCall(detail.call);
        } else if (wasRinging) {
          // Passed on, or taken by a teammate mid-answer
          set({ ringing: get().ringing.filter((call) => call.id !== update.callId) });
        }
        return;
      }

      if (!wasRinging) return;
      set({ ringing: get().ringing.filter((call) => call.id !== update.callId) });
      if (get().answerError?.callId === update.callId) set({ answerError: null });
      // Say who took it, instead of the card just vanishing
      if (update.status === "connected") {
        const detail = await fetchCall(orgId, update.callId);
        if (detail?.call.answeredByName) toast.info(`${detail.call.answeredByName} answered ${callerLabel(detail.call)}`);
      }
    },

    handlePermissionReply: ({ conversationId, granted }) => {
      const label = conversationId.split(":")[0];
      if (!granted) {
        toast.info(`+${label} declined calls from your business`);
        return;
      }
      toast.success(`+${label} allowed calls from your business`, {
        action: { label: "Call now", onClick: () => void get().placeCall(conversationId, `+${label}`) },
      });
    },

    // Brings the call on screen up to date: applies the customer's SDP answer and ends it locally once
    // it is over. Re-reads state after each await, since the teammate may hang up meanwhile.
    syncActive: async () => {
      const { orgId, active } = get();
      if (!orgId || !active) return;
      const detail = await fetchCall(orgId, active.call.id);
      const current = get().active;
      if (!detail || current?.media !== active.media) return;
      if (detail.answerSdp) {
        try {
          await applyCallAnswer(current.media, detail.answerSdp);
        } catch {
          toast.error("Could not connect call audio");
          await get().hangUp();
          return;
        }
      }
      const latest = get().active;
      if (latest?.media !== active.media) return;
      if (!LIVE_STATUSES.includes(detail.call.status)) {
        closeCallMedia(latest.media);
        finish(detail.call);
        outcomeToast(detail.call);
        return;
      }
      set({
        active: {
          ...latest,
          call: detail.call,
          connectedAtLocal: latest.connectedAtLocal ?? (detail.call.status === "connected" ? Date.now() : null),
        },
      });
    },

    answer: async (callId) => {
      const { orgId, active, pending } = get();
      if (!orgId || active || pending) return;
      requestCallNotifications();
      set({ pending: true, answerError: null });
      let media: CallMedia | null = null;
      try {
        const detail = await fetchCall(orgId, callId);
        if (!detail?.offerSdp) {
          set({ ringing: get().ringing.filter((item) => item.id !== callId) });
          toast.info("This call is no longer ringing");
          return;
        }
        const answer = await answerCallOffer(detail.offerSdp, mediaEvents(() => media));
        media = answer.media;
        const response = await hc.api.calls[":callId"].answer.$post({
          param: { callId },
          json: { sdp: answer.sdp },
        }, orgHeaders(orgId));
        if (!response.ok) {
          closeCallMedia(media);
          const message = await errorMessage(response, "Could not answer the call");
          set({ ringing: get().ringing.filter((item) => item.id !== callId) });
          toast.error(message);
          return;
        }
        const { call } = await response.json() as { call: PublicCall };
        set({
          active: { call, media, muted: false, reconnecting: false, connectedAtLocal: Date.now() },
          ringing: get().ringing.filter((item) => item.id !== callId),
        });
        // The customer may have hung up while we were connecting
        void get().syncActive();
      } catch (error) {
        closeCallMedia(media);
        // Still ringing for the customer: keep the card so the teammate can fix the microphone and retry
        set({ answerError: { callId, message: callMediaErrorMessage(error, "answer") } });
      } finally {
        set({ pending: false });
      }
    },

    dismiss: async (callId) => {
      const orgId = get().orgId;
      if (!orgId) return;
      set({ ringing: get().ringing.filter((call) => call.id !== callId), answerError: null });
      const response = await hc.api.calls[":callId"].dismiss.$post({ param: { callId } }, orgHeaders(orgId));
      if (!response.ok && response.status !== 409) toast.error(await errorMessage(response, "Could not pass on the call"));
    },

    declineForEveryone: async (callId) => {
      const orgId = get().orgId;
      if (!orgId) return;
      set({ ringing: get().ringing.filter((call) => call.id !== callId), answerError: null });
      const response = await hc.api.calls[":callId"].decline.$post({ param: { callId } }, orgHeaders(orgId));
      if (!response.ok && response.status !== 409) toast.error(await errorMessage(response, "Could not decline the call"));
    },

    placeCall: async (conversationId, label) => {
      const { orgId, active, pending } = get();
      if (!orgId || active || pending) return "failed";
      requestCallNotifications();
      // The call shows straight away; the microphone prompt and audio setup take a few seconds
      const dialing: Dialing = { conversationId, label, cancelled: false };
      set({ pending: true, dialing, ended: null });
      let media: CallMedia | null = null;
      try {
        const offer = await createCallOffer(mediaEvents(() => media));
        media = offer.media;
        if (get().dialing !== dialing || dialing.cancelled) {
          closeCallMedia(media);
          return "failed";
        }
        const response = await hc.api.calls.$post({
          json: { conversationId, sdp: offer.sdp },
        }, orgHeaders(orgId));
        if (!response.ok) {
          const body = await response.json().catch(() => ({})) as { error?: string; code?: string };
          closeCallMedia(media);
          if (body.code === "no_permission") return "no_permission";
          toast.error(body.error || "Could not place the call");
          return "failed";
        }
        const { call } = await response.json() as { call: PublicCall };
        set({ active: { call, media, muted: false, reconnecting: false, connectedAtLocal: null } });
        if (dialing.cancelled) {
          // Cancelled while Meta was placing it
          await get().hangUp();
          return "failed";
        }
        // Events that arrived before this request returned (the SDP answer, a decline) were not applied
        void get().syncActive();
        return "placed";
      } catch (error) {
        closeCallMedia(media);
        if (!dialing.cancelled) toast.error(callMediaErrorMessage(error, "place"));
        return "failed";
      } finally {
        if (get().dialing === dialing) set({ dialing: null });
        set({ pending: false });
      }
    },

    cancelDialing: () => {
      const dialing = get().dialing;
      if (!dialing) return;
      dialing.cancelled = true;
      set({ dialing: null });
    },

    hangUp: async () => {
      const { orgId, active } = get();
      if (!orgId || !active) return;
      closeCallMedia(active.media);
      finish({ ...active.call, status: active.call.status === "connected" ? "completed" : "missed" });
      const response = await hc.api.calls[":callId"].end.$post({ param: { callId: active.call.id } }, orgHeaders(orgId));
      if (!response.ok) toast.error(await errorMessage(response, "Could not end the call on WhatsApp"));
    },

    endOnUnload: () => {
      const { orgId, active } = get();
      if (orgId && active) void endOnServer(orgId, active.call.id, true).catch(() => undefined);
    },

    toggleMute: () => {
      const active = get().active;
      if (!active) return;
      setCallMuted(active.media, !active.muted);
      set({ active: { ...active, muted: !active.muted } });
    },

    clearEnded: () => set({ ended: null }),

    loadAway: async () => {
      const orgId = get().orgId;
      if (!orgId) return;
      const response = await hc.api.calls.presence.$get({}, orgHeaders(orgId));
      if (response.ok && get().orgId === orgId) set({ away: (await response.json() as { away: boolean }).away });
    },

    setAway: async (away) => {
      const orgId = get().orgId;
      if (!orgId) return;
      const previous = get().away;
      set({ away });
      if (!away) requestCallNotifications();
      const response = await hc.api.calls.presence.$put({ json: { away } }, orgHeaders(orgId));
      if (!response.ok) {
        set({ away: previous });
        toast.error("Could not change your call availability");
      }
    },

    refreshMissed: async () => {
      const orgId = get().orgId;
      if (!orgId) return;
      const seenAt = useCallsSeenStore.getState().seenAt[orgId];
      const query: Record<string, string> = { organizationId: orgId, status: "missed", direction: "inbound", limit: "1" };
      // A first visit counts the last day, not every missed call ever
      query.from = seenAt || new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const response = await hc.api.calls.$get({ query });
      if (response.ok && get().orgId === orgId) set({ missedCount: (await response.json() as { total: number }).total });
    },
  };
});
